import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

import Ind from '../../assets/Ind.png';
import illustration from '../../assets/challanIllustration.png';
import styles from './CheckChallan.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.rtoxpert.com';

function CheckChallan() {
  const navigate = useNavigate();
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateVehicleNumber = (number) => {
    const cleanedNumber = number.replace(/\s/g, "").toUpperCase();

    const regex = /^[A-Z]{2}[0-9]{1,2}[A-Z]{0,3}[0-9]{1,4}$/;

    const validStateCodes = [
      "AN", "AP", "AR", "AS", "BR", "CH", "CG", "DD", "DL", "GA", "GJ", "HR", "HP",
      "JH", "JK", "KA", "KL", "LD", "MP", "MH", "MN", "ML", "MZ", "NL", "OD", "PY",
      "PB", "RJ", "SK", "TN", "TS", "TR", "UP", "UK", "WB"
    ];

    if (!cleanedNumber) {
      return "Please enter a vehicle number";
    }

    if (!regex.test(cleanedNumber)) {
      return "Invalid vehicle number format. Please enter a valid Indian vehicle number (e.g., DL3SDW2595, UP57BX1666).";
    }

    const stateCode = cleanedNumber.substring(0, 2);
    if (!validStateCodes.includes(stateCode)) {
      return "Invalid state code. Please use a valid Indian state code (e.g., DL, MH, UP).";
    }

    return "";
  };

  // Step 1: POST /verify-rc to get request_id
  const startVerification = async (rcNumber) => {
    const token = localStorage.getItem("token");
    const headers = new Headers({ "Content-Type": "application/json" });
    if (token) headers.append("Authorization", `Bearer ${token}`);

    const response = await fetch(`${API_BASE_URL}/api/rc/verify-rc`, {
      method: "POST",
      headers,
      body: JSON.stringify({ rc_number: rcNumber }),
    });
    if (!response.ok) throw new Error("Failed to start verification");

    const data = await response.json();
    if (data.data && data.data.rc_data) {
      return { immediateData: data.data.rc_data };
    }
    if (!data.request_id) throw new Error("Missing request_id from verification API");
    return data.request_id;
  };

  // Step 2: Poll GET /verification-status/:request_id until done
  const pollVerificationResult = async (requestId, maxTries = 10, intervalMs = 3000) => {
    const token = localStorage.getItem("token");
    const headers = new Headers({ "Content-Type": "application/json" });
    if (token) headers.append("Authorization", `Bearer ${token}`);

    for (let i = 0; i < maxTries; i++) {
      const response = await fetch(`${API_BASE_URL}/api/rc/verification-status/${requestId}`, {
        method: "GET",
        headers,
      });
      if (!response.ok) throw new Error("Failed to fetch verification status");

      const data = await response.json();

      if ((response.status === 200 || data.status === 200 || data.status === 'success' || data.status === 'completed') && (data.data?.rc_data || data.rc_data)) {
        return data.data?.rc_data || data.rc_data;
      } else if (response.status === 400 || response.status === 404 || data.status === 400 || data.status === 404) {
        throw new Error(data.message || "Verification failed or not found");
      }

      if (data.data?.rc_data) return data.data.rc_data;

      // status 202 = still processing
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    throw new Error("Verification timed out");
  };

  // Combined fetchVehicleDetails using above steps
  const fetchVehicleDetails = async (rcNumber) => {
    try {
      const startResult = await startVerification(rcNumber);
      let rcResult;
      if (startResult.immediateData) {
        rcResult = startResult.immediateData;
      } else {
        rcResult = await pollVerificationResult(startResult);
      }
      return {
        status: 200,
        data: {
          code: "1000",
          rc_data: rcResult
        }
      };
    } catch (err) {
      throw err;
    }
  };

  // Step 1: POST to start challan verification via your backend
  const startChallanVerification = async (rcNumber, chassisNumber) => {
    const token = localStorage.getItem("token");
    const headers = new Headers({ "Content-Type": "application/json" });
    if (token) headers.append("Authorization", `Bearer ${token}`);

    // Get last 5 digits of chassis number
    const last5Chassis = chassisNumber.slice(-5);

    const requestBody = {
      rc_number: rcNumber,
      chassis_number: last5Chassis
    };

    const requestOptions = {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody),
      redirect: 'follow'
    };

    try {
      // Call your backend proxy endpoint instead of IdFY directly
      const response = await fetch(`${API_BASE_URL}/api/challan/verify-challan`, requestOptions);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(`Failed to start challan verification: ${result.message || response.statusText}`);
      }

      if (result.data) {
        return { immediateData: result.data };
      }

      if (!result.request_id) {
        throw new Error("Missing request_id from challan verification API");
      }

      return result.request_id;
    } catch (error) {
      throw error;
    }
  };

  // Step 2: Poll for challan verification results via your backend
  const pollChallanResult = async (requestId, rcNumber, chassisNumber, maxTries = 15, intervalMs = 3000) => {
    const token = localStorage.getItem("token");
    const headers = new Headers();
    if (token) headers.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      headers: headers,
      redirect: 'follow'
    };

    for (let i = 0; i < maxTries; i++) {
      try {
        // Call your backend proxy endpoint with query parameters
        const url = `${API_BASE_URL}/api/challan/challan-details/${requestId}?rc_number=${rcNumber}&chassis_number=${chassisNumber}`;
        const response = await fetch(url, requestOptions);
        const result = await response.json();

        if (!response.ok) {
          throw new Error(`Failed to fetch challan status: ${result.message || response.statusText}`);
        }

        // Check if the task is completed
        if ((response.status === 200 || result.status === 200 || result.status === 'completed' || result.status === 'success') && result.data) {
          return result.data;
        } else if (response.status === 404 || result.status === 404) {
          // No challans found
          return {
            rc_number: rcNumber,
            challan_summary: {
              total_challan_number: "0",
              total_pending_challan: "0",
              total_paid_challan: "0"
            },
            challan_details: [],
            status: "id_not_found"
          };
        } else if (response.status === 202 || result.status === 202 || result.status === 'processing') {
          // Still processing, wait and try again
          await new Promise(resolve => setTimeout(resolve, intervalMs));
          continue;
        } else {
          throw new Error(result.message || "Challan verification failed");
        }
      } catch (error) {
        if (i === maxTries - 1) {
          throw error;
        }
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
    }

    throw new Error("Challan verification timed out");
  };

  // Combined fetchChallanDetails using backend proxy
  const fetchChallanDetails = async (rcNumber, chassisNumber) => {
    try {
      const startResult = await startChallanVerification(rcNumber, chassisNumber);
      let challanResult;
      if (startResult.immediateData) {
        challanResult = startResult.immediateData;
      } else {
        challanResult = await pollChallanResult(startResult, rcNumber, chassisNumber);
      }
      return {
        status: 200,
        data: challanResult
      };
    } catch (error) {
      // Return empty challan data if API fails
      return {
        status: 200,
        data: {
          rc_number: rcNumber,
          challan_summary: {
            total_challan_number: "0",
            total_pending_challan: "0",
            total_paid_challan: "0"
          },
          challan_details: [],
          status: "id_not_found"
        }
      };
    }
  };

  const handleSearchClick = async () => {
    const validationError = validateVehicleNumber(vehicleNumber);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setLoading(true);

    const cleanedVehicleNumber = vehicleNumber.replace(/\s+/g, '').toUpperCase();

    try {
      // Fetch vehicle details using the RC API
      const vehicleDetails = await fetchVehicleDetails(cleanedVehicleNumber);

      if (vehicleDetails.status === 200 && vehicleDetails.data.code === "1000") {
        const { chassis_number } = vehicleDetails.data.rc_data.vehicle_data;

        if (!chassis_number) {
          throw new Error('Chassis number not found in vehicle details');
        }

        // Fetch challan details using backend proxy
        const challanDetails = await fetchChallanDetails(
          cleanedVehicleNumber,
          chassis_number
        );

        // Navigate to challan details with all the data
        setTimeout(() => {
          navigate('/challandetails', {
            state: {
              vehicleNumber: cleanedVehicleNumber,
              vehicleDetails: vehicleDetails.data.rc_data,
              challanDetails: challanDetails.data
            }
          });
          setLoading(false);
        }, 2000);
      } else {
        setError('Vehicle details not found. Please check the vehicle number.');
        setLoading(false);
      }
    } catch (error) {
      setError(`Failed to fetch details: ${error.message}`);
      setLoading(false);
    }
  };

  return (
    <div className={styles.checkChallan}>
      <h1 className={styles.title}>
        Enter the vehicle number to check challan
      </h1>

      <div className={styles.searchContainer}>
        <div className={styles.inputPrefix}>
          <img src={Ind} alt="IND" className={styles.indIcon} />
          <div className={styles.divider} />
        </div>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="e.g., MH15CV2211, DL12AA1234"
          value={vehicleNumber}
          onChange={(e) => {
            setVehicleNumber(e.target.value.toUpperCase());
            setError('');
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSearchClick();
            }
          }}
          disabled={loading}
        />
        <button
          className={styles.searchBtn}
          onClick={handleSearchClick}
          disabled={loading}
        >
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
        </button>
      </div>
      {error && (
        <p className={styles.errorMessage} style={{ color: "red", marginTop: "10px" }}>
          {error}
        </p>
      )}
      {loading && (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Fetching vehicle and challan details...</p>
        </div>
      )}

      <p className={styles.disclaimer}>
        This service is only accessible for automobiles in India, and you must
        agree to our terms and conditions to use this website.
      </p>

      <img
        src={illustration}
        alt="Illustration"
        className={styles.illustration}
      />
    </div>
  );
}

export default CheckChallan;
